import * as React from 'react'
import { useEffect, useState } from 'react'
import { BottomBar, Footer, Header, Sidebar } from '@/components'
import { GracePeriodBanner } from '@/components/GracePeriodBanner'
import { useAuth } from '@/context'
import { cn } from '@/lib/utils'

export interface MainProps extends React.HTMLAttributes<HTMLDivElement> {
	extraClasses?: string
	currentPage?: string
	headerTitle?: string
	hideFooter?: boolean
	noScroll?: boolean
	/** Remove padding on noScroll main content (e.g. toolbar flush under header, panel to the page edge). */
	flushContentTop?: boolean
	onHeaderBack?: () => void
}

const Main = React.forwardRef<HTMLDivElement, MainProps>(
	(
		{
			children,
			extraClasses,
			currentPage,
			headerTitle,
			hideFooter = false,
			noScroll = false,
			flushContentTop = false,
			onHeaderBack,
			...props
		},
		ref
	) => {
		const { user } = useAuth()
		const [isVisible, setIsVisible] = useState(false)

		useEffect(() => {
			// Trigger animation after component mounts
			const timer = setTimeout(() => setIsVisible(true), 100)
			return () => clearTimeout(timer)
		}, [])

		if (!user) return null

		return (
			<div
				ref={ref}
				className={cn('flex h-full min-h-0 flex-col overflow-hidden bg-gray-50', extraClasses)}
				{...props}
			>
				<div className="shrink-0">
					<Header isLogoCentered={true} headerTitle={headerTitle} onBack={onHeaderBack} />
				</div>

				<div className="flex min-h-0 flex-1 overflow-hidden">
					<Sidebar currentPage={currentPage} />

					<div
						className={cn(
							'flex min-h-0 flex-1 flex-col overflow-x-hidden w-full',
							noScroll ? 'overflow-y-auto md:overflow-hidden' : 'overflow-y-auto'
						)}
					>
						<GracePeriodBanner />
						<div
							className={cn(
								'flex flex-col transition-[opacity,transform] duration-700 ease-out',
								noScroll
									? 'min-h-full md:flex-1 md:min-h-0 md:overflow-hidden'
									: 'min-h-full overflow-y-auto',
								isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
							)}
						>
							<main
								className={cn(
									'flex flex-col gap-1',
									noScroll
										? cn('md:flex-1 md:min-h-0 md:overflow-hidden', flushContentTop ? 'p-0' : 'p-3')
										: 'p-5'
								)}
							>
								{children}
							</main>

							{!hideFooter && (
								<div className="mt-auto">
									<Footer />
								</div>
							)}
						</div>

						<BottomBar currentPage={currentPage} />
					</div>
				</div>
			</div>
		)
	}
)
Main.displayName = 'Main'

export { Main }
